import { useLogoutMutation } from "../api/auth";
import { useAuthSelector } from "../store/auth";
import { resetAllStores } from "../store/middleware/resetMiddleware";
import { authTokenService, clearAuthToken } from "../utilities/authTokenService";
import { clearAppCookies } from "../utilities/cookiesService";
import { enqueueSnackbar } from "notistack";
import { enqueueUniqueSnackbar } from "../utilities/snackbarUtils";
import { useQueryClient } from "@tanstack/react-query";
import { handleError } from "../utilities/errors";

/**
 * Hook to log out the current user.
 */
export function useLogout() {
  const { accessToken } = authTokenService();
  const { user } = useAuthSelector();

  const { mutateAsync: logoutMutation, error, isError, isPending } = useLogoutMutation();
  const queryClient = useQueryClient();

  // If there's a mutation error, we normalize it.
  // We keep the "throw" pattern if the user wants it to hit an Error Boundary,
  // but we ensure it's a structured AppError.
  if (isError && error) {
    throw handleError(error);
  }

  /**
   * Call server logout and cleanup user auth tokens, cookies and state.
   * after successful login user will redirect to login page.
   * **/
  async function logout(expired?: boolean, onSuccess?: () => void) {
    console.log("🚪 LOGOUT: Starting comprehensive logout cleanup", { expired });
    console.log("🚪 LOGOUT: Current user before logout:", {
      email: user?.email,
      is_staff: user?.is_staff,
      is_superuser: user?.is_superuser
    });

    // 0. Call backend logout endpoint FIRST to invalidate server session
    // Skip if we know the session is already expired
    if (accessToken && !expired) {
      try {
        await logoutMutation(undefined);
        console.log("LOGOUT: Backend logout successful");

        clearAuthStates();
        redirectToNextURL();
      } catch (err: unknown) {
        const appError = handleError(err);
        // If the error is just telling us we're already expired, skip the toast
        if (appError.message !== "Authentication expired") {
          enqueueUniqueSnackbar(enqueueSnackbar, appError.message || "Unable to logout from the server.", {
            variant: "error"
          });
        }
        console.warn("LOGOUT: Backend logout failed:", appError.message);
      }
    } else {
      console.log("LOGOUT: Skipping backend logout (expired session or no token)");
      clearAuthStates();
      redirectToNextURL();
    }

    // function to clear localStorage state,
    // cookies, zustand store and query cache
    function clearAuthStates() {
      // 1. Clear ALL registered stores using the global cleanup system
      console.log("🧹 LOGOUT: Clearing all stores");
      resetAllStores();

      // 2. Clear all localStorage comprehensively
      // This includes: user, accessToken, refreshToken, admin settings, signed URLs, etc.
      console.log("🧹 LOGOUT: Clearing localStorage");
      const sessionKeys = Object.keys(localStorage);
      console.log("🧹 LOGOUT: localStorage keys before clear:", sessionKeys);
      localStorage.clear();

      // Also clear any localStorage if used (future-proofing)
      try {
        // Clear specific localStorage items that might exist
        console.log("🧹 LOGOUT: Clearing localStorage items");
        localStorage.removeItem("tokenData"); // From Fulcrum integration
      } catch (error) {
        console.warn("⚠ LOGOUT: Error clearing localStorage:", error);
      }

      // 3. Clear app cookies
      console.log("🍪 LOGOUT: Clearing all cookies");
      clearAppCookies();

      // 4. Clear one time decrypted tokens from `authTokenService`
      console.log("LOGOUT: Clearing in-memory decrypted auth tokens");
      clearAuthToken();

      localStorage.clear();
      queryClient.clear();
    }

    function redirectToNextURL() {
      const redirectURL = loginRedirectURL();
      if (window.location.pathname !== "login") {
        window.location.replace(redirectURL);
      }
    }
  }

  return { logout, isPending, isError, error } as const;
}

export function loginRedirectURL() {
  const { pathname, search } = window.location;
  const url = new URL("/login", window.location.origin);
  const redirectTo = `${pathname}${search}`;
  url.searchParams.set("redirect", redirectTo);

  return `${url.pathname}${url.search}`;
}
